import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { BrandLoader } from "@/components/brand";
import type { SnippetAction } from "@/features/caseIde/pdfSnippet/toolbar";
import type { PdfViewerZoomMode } from "@/features/caseIde/pdfViewer/types";

const PdfViewer = lazy(() =>
  import("@/features/caseIde/pdfViewer/PdfViewer").then((m) => ({
    default: m.PdfViewer
  }))
);

type PdfPreviewProps = {
  /** A File, a Blob, or a URL to fetch one from. */
  file: string | File | Blob | null;
  ariaLabel?: string;
  emptyText?: string;
  unavailableText?: string;
  defaultZoomMode?: PdfViewerZoomMode;
  scrollToPageIndex?: number | null;
  /** Change it to repeat a jump to the same page. */
  scrollToPageKey?: string | number | null;
  /**
   * Fires with the 1-based page nearest the top of the viewport. Packet Mode
   * uses it to highlight whichever document owns the page you scrolled to.
   */
  onVisiblePageChange?: (pageNumber: number) => void;
  /** Replaces the viewer's own download, e.g. to fetch the original file. */
  onDownload?: () => void | Promise<void>;
  /** The name the viewer's own download saves as; a File's name by default. */
  downloadName?: string;
  /** Host buttons at the end of the toolbar. */
  actions?: SnippetAction[];
  showControls?: boolean;
};

type FileState =
  | { status: "loading" }
  | { status: "ready"; blob: Blob }
  | { status: "failed" };

/** The viewer reads Blobs only, so a URL is fetched into one. */
const useFileBlob = (file: PdfPreviewProps["file"]): FileState | null => {
  const [fetched, setFetched] = useState<{
    url: string;
    state: FileState;
  } | null>(null);
  useEffect(() => {
    if (typeof file !== "string") return undefined;
    const controller = new AbortController();
    setFetched({ url: file, state: { status: "loading" } });
    fetch(file, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.blob();
      })
      .then((blob) =>
        setFetched({ url: file, state: { status: "ready", blob } })
      )
      .catch(() => {
        if (controller.signal.aborted) return;
        setFetched({ url: file, state: { status: "failed" } });
      });
    return () => controller.abort();
  }, [file]);
  if (!file) return null;
  if (typeof file !== "string") return { status: "ready", blob: file };
  return fetched?.url === file ? fetched.state : { status: "loading" };
};

const Notice = ({ text }: { text: string }) => (
  <div className="flex size-full items-center justify-center px-4 text-center">
    <p className="text-sm text-fg-3">{text}</p>
  </div>
);

const Loading = () => (
  <div className="flex size-full items-center justify-center">
    <BrandLoader label="Loading the PDF" />
  </div>
);

/**
 * A read-only PDF preview on the Case IDE's EmbedPDF viewer, for any surface
 * that doesn't draw over the pages (citations use `CitationPdfViewer`).
 */
export const PdfPreview = ({
  file,
  ariaLabel = "Document Preview",
  emptyText = "No file available to preview.",
  unavailableText = "Preview unavailable.",
  defaultZoomMode = "page-fit",
  scrollToPageIndex = null,
  scrollToPageKey = null,
  onVisiblePageChange,
  onDownload,
  downloadName,
  actions,
  showControls = true
}: PdfPreviewProps) => {
  const state = useFileBlob(file);

  const downloadRef = useRef(onDownload);
  downloadRef.current = onDownload;
  const hasDownload = Boolean(onDownload);
  const toolbarActions = useMemo(() => {
    const download: SnippetAction[] = hasDownload
      ? [
          {
            id: "download",
            label: "Download",
            icon: "download",
            onSelect: () => void downloadRef.current?.()
          }
        ]
      : [];
    return [...download, ...(actions ?? [])];
  }, [hasDownload, actions]);

  if (!state) return <Notice text={emptyText} />;
  if (state.status === "failed") return <Notice text={unavailableText} />;
  if (state.status === "loading") return <Loading />;

  return (
    <div className="size-full min-h-0" role="region" aria-label={ariaLabel}>
      <Suspense fallback={<Loading />}>
        <PdfViewer
          file={state.blob}
          ariaLabel={ariaLabel}
          emptyText={emptyText}
          unavailableText={unavailableText}
          defaultZoomMode={defaultZoomMode}
          scrollToPageIndex={scrollToPageIndex}
          scrollToPageKey={scrollToPageKey}
          onVisiblePageChange={onVisiblePageChange}
          downloadName={
            downloadName ?? (file instanceof File ? file.name : undefined)
          }
          actions={toolbarActions}
          toolbar={showControls}
          hideDownload={hasDownload}
        />
      </Suspense>
    </div>
  );
};
